import React, { useEffect, useMemo, useState } from 'react';
import { PageTemplate } from 'components/PageTemplate';
import { get, orderBy } from 'lodash';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { TableHead } from '@bb-ui/react-library/dist/components/TableHead';
import { TableRow } from '@bb-ui/react-library/dist/components/TableRow';
import { SortableTableHeaderCell } from '@bb-ui/react-library/dist/components/SortableTableHeaderCell';
import { TableCell } from '@bb-ui/react-library/dist/components/TableCell';
import { PaginatedTable } from 'components/PaginatedTable';
import {
  OnSortChangedParams,
  SortDirection,
} from '@bb-ui/react-library/dist/components/SortableTable/SortableTable.types';
import { useTranslation } from 'react-i18next';
import {
  createStyles,
  makeStyles,
  Theme,
  Chip,
  CircularProgress,
  TextField,
  MenuItem,
  SearchBox,
  Box,
} from '@bb-ui/react-library';
import { Avatar } from '@bb-ui/react-library/dist/components/Avatar';
import { OutlineButton } from '@bb-ui/react-library/dist/components/Button';
import useRestApi from 'hooks/useRestApi';
import { apiUrl } from 'utils/apiUrl';
import { ArtifactType, IArtifactData } from 'App.types';
import { useAppConfigContext } from 'contexts/AppConfigContext';
import { ArtifactPagination } from './Pagination';

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    ChipApproved: {
      backgroundColor: theme.palette.success.light,
    },
    ChipUnapproved: {
      backgroundColor: theme.palette.secondary.main,
    },
    ActionBtn: {
      minHeight: '36px',
    },
    artifactSelect: {
      backgroundColor: theme.palette.background.paper,
      minWidth: 150,
    },
    filterSection: {
      marginBottom: theme.spacing(2),
      display: 'flex',
      gap: 10,
    },
    loader: {
      textAlign: 'center',
    },
    arifactTableContainer: {
      '& [data-testid="paginated-table-items-per-page"]': {
        display: 'none',
      },
    },
  }),
);

const artifacts: ArtifactType[] = ['learn-installer', 'ultra-ui', 'ultra-router'];
const RESULTS_COUNT = 10; // default records per page

export const ArtifactList: React.FunctionComponent = (props) => {
  const { t } = useTranslation();
  const classes = useStyles(props);
  const appConfig = useAppConfigContext();
  const [artifactData, setArtifactData] = useState<IArtifactData[]>([]);
  const [selectedArtifact, setSelectedArtifact] = useState<string>(artifacts[0]);
  const [searchKey, setSearchkey] = useState<string>('');
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [searchToken, setSearchNextToken] = useState<string | null>(null);
  const [approvedArtifact, setApprovedArtifact] = useState<string | null>(null);
  const [approveLoader, setApproveLoader] = useState<boolean>(false);
  const [sortParams, setSortParams] = useState<Partial<OnSortChangedParams>>({});

  const getQueryParams = new URLSearchParams({
    artifact_type: selectedArtifact,
    results_count: RESULTS_COUNT.toString(),
  });

  const searchQueryParams = new URLSearchParams({
    artifact_type: selectedArtifact,
    artifact_name: `${encodeURIComponent(searchKey)}`,
    results_count: RESULTS_COUNT.toString(),
  });

  if (nextToken) {
    getQueryParams.append('next_token', nextToken);
  }

  if (searchToken) {
    searchQueryParams.append('next_token', searchToken);
  }

  const getUrl = apiUrl('fcc', `artifacts?${getQueryParams.toString()}`);
  const searchUrl = apiUrl('fcc', `artifacts?${searchQueryParams.toString()}`);

  const {
    data,
    error,
    loading,
    fetch: getArtifacts,
  } = useRestApi(getUrl, {
    manual: true,
  });

  const {
    succeededRequests,
    clearSucceededRequests,
    doPatch: approveArtifact,
  } = useRestApi(apiUrl('fcc', `artifacts`), {
    manual: true,
  });

  const {
    data: searchResp,
    loading: searchLoading,
    fetch: searchArtifacts,
  } = useRestApi(searchUrl, {
    manual: true,
  });

  useEffect(() => {
    setArtifactData((prevArtifacts: any) => {
      let artifactData;
      if (searchKey) {
        artifactData = [...(prevArtifacts || []), ...(searchResp?.items || [])];
        setSearchNextToken(searchResp?.next_token);
      } else {
        artifactData = [...(prevArtifacts || []), ...(data?.items || [])];
        setNextToken(data?.next_token);
      }
      return artifactData;
    });
  }, [data, searchResp]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getArtifacts();
  }, [selectedArtifact]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (succeededRequests?.length) {
      const updatedArtifactData = artifactData;
      const data = succeededRequests[0]?.response?.data;

      const indx = updatedArtifactData.findIndex(
        (artifacts: any) => artifacts.artifact_key === approvedArtifact,
      );

      updatedArtifactData[indx] = data;

      setArtifactData(updatedArtifactData);
      setApproveLoader(false);
      clearSucceededRequests();
    }
  }, [succeededRequests]); // eslint-disable-line react-hooks/exhaustive-deps

  let content: React.ReactElement;

  // SORTING
  const sortedArtifactList = useMemo(() => {
    const { sortColumnId, sortDirection } = sortParams;

    if (sortColumnId && sortDirection && ['artifact_key'].includes(sortColumnId)) {
      return orderBy(
        artifactData,
        [(artiact) => get(artiact, sortColumnId).toLowerCase()],
        sortDirection,
      );
    }
    if (sortColumnId === 'approval') {
      return orderBy(artifactData, [(artiact) => artiact.approval === 'yes'], sortDirection);
    }
    return artifactData;
  }, [artifactData, sortParams]);

  const getAriaSortMessage = (columnId?: string, sortDirection?: SortDirection) => {
    const columnLabel = t(`artifactLists.${columnId}`);
    const orderLabel =
      sortDirection === 'asc'
        ? t('global.paginatedTable.ascending')
        : t('global.paginatedTable.descending');
    return t('global.paginatedTable.sortedAriaMessage', { columnLabel, orderLabel });
  };

  const getPageTitle = (): string => {
    let localeTitle = t('artifactLists.pageTitle');

    switch (selectedArtifact) {
      case 'learn-installer':
        localeTitle = t('artifactLists.pageTitleLearn');
        break;
      case 'ultra-ui':
        localeTitle = t('artifactLists.pageTitleUltra');
        break;
      case 'ultra-router':
        localeTitle = t('artifactLists.pageTitleLua');
        break;
      default:
        break;
    }

    return localeTitle;
  };

  // handle artifact select dropdown
  const handleArtifactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setArtifactData([]);
    setSearchkey('');
    setNextToken('');
    setSearchNextToken('');
    setSelectedArtifact(e.target.value);
  };

  // parse artifact version from s3 path
  const getVersion = (artifactKey: string): string =>
    selectedArtifact === 'ultra-router'
      ? artifactKey.split('/')[1].replace(/\.tgz$/, '')
      : artifactKey.split('/')[2].replace(/\.zip$/, '');

  /**
   * Manage approval for artifacts when action btn clicked
   * @param artifactKey Artifact S3 Key
   * @param approval Approval status 'yes' or 'no'
   */
  const manageApproval = (artifactKey: string, approval: string | null): void => {
    try {
      setApprovedArtifact(artifactKey);
      setApproveLoader(true);
      if (!approval || approval === 'no') {
        approveArtifact(encodeURIComponent(artifactKey), {
          approval: 'yes',
        });
        return;
      }

      approveArtifact(encodeURIComponent(artifactKey), {
        approval: 'no',
      });
    } catch (error) {
      console.error('Error while approving artifacts', error);
      setApprovedArtifact(null);
      setApproveLoader(false);
    }
  };

  // handle search change
  const handleSearchChange = (value: any): void => {
    setSearchkey(value);
  };

  // handle when hit Enter key is pressed
  const handleSearch = () => {
    if (!searchKey) return;
    setArtifactData([]);
    setNextToken('');
    searchArtifacts();
  };

  // handle when clearing search box
  const clearSearch = (): void => {
    setArtifactData([]);
    setSearchNextToken(null);
    getArtifacts();
  };

  const isloadMoreBtnDisabled = (): boolean => {
    if (searchKey && searchToken) return false;
    if (!searchKey && nextToken) return false;
    return true;
  };

  const isDisableActionBtn = (): boolean =>
    !!(appConfig.stage === 'dev' || appConfig.stage === 'int');

  const artifactList = () => (
    <div data-testid="fnds-artifact-list-data-table" className={classes.arifactTableContainer}>
      <PaginatedTable
        onSortChanged={(sortParams) => {
          setSortParams(sortParams);
          return true;
        }}
        getSortChangedAriaMessage={getAriaSortMessage}
        sortedData={sortedArtifactList}
        noMatchesMessage={(searchExpression) => t('artifactLists.noMatch', { searchExpression })}
        customPagination={true}
        renderHead={() => (
          <TableHead>
            <TableRow>
              <SortableTableHeaderCell
                id="artifact-list-table-header-version"
                columnId="artifact_key"
                tooltip={t('artifactLists.sortByVersion')}
              >
                {t('artifactLists.version')}
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                id="artifact-list-table-header-approval"
                columnId="approval"
                tooltip={t('artifactLists.sortByStatus')}
              >
                {t('artifactLists.status')}
              </SortableTableHeaderCell>
              <TableCell id="artifact-list-table-header-approver" tabIndex={-1} role="columnheader">
                {t('artifactLists.approver')}
              </TableCell>
              <TableCell id="artifact-list-table-header-action" tabIndex={-1} role="columnheader">
                {t('artifactLists.action')}
              </TableCell>
            </TableRow>
          </TableHead>
        )}
        renderRow={(artifact: IArtifactData, index) => (
          <TableRow
            hover
            key={artifact.artifact_key}
            aria-rowindex={index + 1}
            data-testid={`artifact-list-table-row-${artifact.artifact_key}`}
          >
            <TableCell aria-colindex={1} tabIndex={-1}>
              {getVersion(artifact.artifact_key)}
            </TableCell>
            <TableCell aria-colindex={2} tabIndex={-1}>
              <Chip
                label={artifact.approval === 'yes' ? 'APPROVED' : 'NOT APPROVED'}
                className={
                  artifact.approval === 'yes' ? classes.ChipApproved : classes.ChipUnapproved
                }
              />
            </TableCell>
            <TableCell aria-colindex={2} tabIndex={-1}>
              <Chip avatar={<Avatar size="small" />} label={artifact.approver ?? 'Unknown'} />
            </TableCell>
            <TableCell aria-colindex={4} tabIndex={-1}>
              <OutlineButton
                size="small"
                className={classes.ActionBtn}
                disabled={isDisableActionBtn()}
                onClick={() => manageApproval(artifact.artifact_key, artifact.approval)}
              >
                {artifact.approval === 'yes' ? 'Unapprove' : 'Approve'}
              </OutlineButton>
            </TableCell>
          </TableRow>
        )}
      />
    </div>
  );

  const erroMessage = () => (
    <Typography data-testid="artifact-info-error">{t('global.fetchError')}</Typography>
  );

  const noArtifactListMessage = () =>
    (content = (
      <Typography data-testid="artifact-info-no-data">{t('artifactLists.noData')}</Typography>
    ));

  const loader = () => (
    <div className={classes.loader}>
      <CircularProgress
        ariaLabel={t('global.loadingIndicator.loading').toString()}
        data-testid="loading-artifact-info"
      />
    </div>
  );

  if (error) {
    content = erroMessage();
  } else if (loading || searchLoading || approveLoader) {
    content = loader();
  } else if (!artifactData || !artifactData.length) {
    content = noArtifactListMessage();
  } else {
    content = artifactList();
  }

  const filterSection: React.ReactElement = (
    <Box className={classes.filterSection}>
      <TextField
        select
        data-testid="artifact-select"
        label={t('artifactLists.selectArtifact')}
        value={selectedArtifact}
        onChange={handleArtifactChange}
        className={classes.artifactSelect}
        floatingLabel
      >
        {artifacts.map((artifact) => (
          <MenuItem key={artifact} value={artifact}>
            {artifact}
          </MenuItem>
        ))}
      </TextField>
      <SearchBox
        label={t('artifactLists.searchLabel')}
        inputId="paginated-table-search-input"
        value={searchKey}
        onChange={handleSearchChange}
        onSearch={() => handleSearch()}
        onClear={() => clearSearch()}
      />
    </Box>
  );

  return (
    <PageTemplate title={getPageTitle()}>
      {!loading ? filterSection : ''}
      {content}
      {!(loading || searchLoading) ? (
        <ArtifactPagination
          isDisabled={isloadMoreBtnDisabled()}
          getArtifacts={getArtifacts}
          searchArtifacts={searchArtifacts}
          apiType={searchKey ? 'search' : 'get'}
        />
      ) : (
        ''
      )}
    </PageTemplate>
  );
};
